/**
 * Step 3 — The Business Case.
 *
 * Three rich-text answers (problem / value / alternatives), each with the source's
 * guidance and a collapsible worked example. The worked examples are the teaching part
 * of this step and live verbatim in constants/aiGovernance.
 *
 * The editors are the shared RichTextField, which is the same ReactQuill toolbar the
 * rest of Nimbus uses (bold/italic/underline, ordered/bullet list, link, clean).
 */

import React, { useState } from 'react';
import { RichTextField } from '../../../components/RichTextField';
import { BUSINESS_CASE_FIELDS, BUSINESS_CASE_FOOTNOTE } from '../../../constants/aiGovernance';
import type { BusinessCaseFieldSpec } from '../../../constants/aiGovernance';
import type { StepProps } from './types';

const Required: React.FC = () => <span style={{ color: 'var(--bs-danger)' }}>*</span>;

interface GuidedFieldProps {
  spec: BusinessCaseFieldSpec;
  value: string;
  onChange: (html: string) => void;
}

const GuidedField: React.FC<GuidedFieldProps> = ({ spec, value, onChange }) => {
  const [showExample, setShowExample] = useState(false);
  const exampleId = `ai-uc-${spec.field}-example`;

  return (
    <div className="np-field mb-4">
      <div className="d-flex align-items-baseline justify-content-between gap-2 flex-wrap">
        {/* No htmlFor: the control is a Quill editor, not a labellable form element. */}
        <label className="mb-0">{spec.label} {spec.required && <Required />}</label>
        <button
          type="button"
          className="np-btn np-btn-ghost np-btn-sm"
          aria-expanded={showExample}
          aria-controls={exampleId}
          onClick={() => setShowExample(open => !open)}
        >
          <i className={showExample ? 'ri-eye-off-line' : 'ri-lightbulb-line'}></i>
          {showExample ? 'Hide example' : 'See an example'}
        </button>
      </div>

      <p className="np-hint mt-1 mb-2">{spec.guidance}</p>

      {showExample && (
        <div className="np-banner np-tone-info mb-2" id={exampleId}>
          <i className="ri-lightbulb-line"></i>
          <span style={{ fontStyle: 'italic' }}>{spec.example}</span>
        </div>
      )}

      <RichTextField value={value} onChange={onChange} placeholder={spec.placeholder} height={130} />
    </div>
  );
};

const Step3BusinessCase: React.FC<StepProps> = ({ useCase, update }) => (
  <>
    <p className="np-sub mb-4">
      Help us understand <em>why</em> this AI is needed. Be specific — vague answers make the
      approval process harder.
    </p>

    {BUSINESS_CASE_FIELDS.map(spec => (
      <GuidedField
        key={spec.field}
        spec={spec}
        value={useCase[spec.field] || ''}
        onChange={html => update({ [spec.field]: html })}
      />
    ))}

    <div className="np-banner np-tone-warning">
      <i className="ri-alert-line"></i>
      <span>{BUSINESS_CASE_FOOTNOTE}</span>
    </div>
  </>
);

export default Step3BusinessCase;
